import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { collection, doc, onSnapshot } from 'firebase/firestore';
import PublicLayout from '../../components/public/PublicLayout';
import PageHero from '../../components/public/PageHero';
import { db } from '../../firebase';
import { MILLIONAIRES_CONNECT_EXTERNAL_ENROLL_URL } from '../../publicSite/admissionCodes';
import { addToCart } from '../../publicSite/cart';
import { APPLY_ROUTE, withBase } from '../../publicSite/config';
import { normalizeFacultiesContent } from '../../publicSite/facultyContent';
import { faculties as defaultFaculties } from '../../publicSite/facultyData';
import { millionairesConnectLessons } from '../../data/economyPrograms';
import { usePageMeta } from '../../publicSite/usePageMeta';

const toYoutubeEmbed = (url = '') => {
  const value = String(url).trim();
  if (value.includes('youtube.com/embed/')) return value;
  const watchMatch = value.match(/[?&]v=([^&]+)/);
  if (watchMatch?.[1]) return `https://www.youtube.com/embed/${watchMatch[1]}`;
  const shortMatch = value.match(/youtu\.be\/([^?&]+)/);
  if (shortMatch?.[1]) return `https://www.youtube.com/embed/${shortMatch[1]}`;
  return '';
};

const readPurchasedProducts = () => {
  try {
    const saved = JSON.parse(localStorage.getItem('shem_purchased_products') || '[]');
    return Array.isArray(saved) ? saved : [];
  } catch {
    return [];
  }
};

const slugify = (value = '') => String(value)
  .toLowerCase()
  .trim()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-|-$/g, '');

export default function FacultyDetail() {
  const { slug } = useParams();
  const useFirebase = process.env.REACT_APP_USE_FIREBASE === 'true';
  const [faculties, setFaculties] = useState(() => normalizeFacultiesContent(defaultFaculties));
  const [courses, setCourses] = useState([]);
  const [addedProductId, setAddedProductId] = useState('');
  const [productFormats, setProductFormats] = useState({});
  const [purchasedProducts] = useState(readPurchasedProducts);
  const faculty = useMemo(() => faculties.find((item) => item.slug === slug), [faculties, slug]);

  useEffect(() => {
    if (!useFirebase) return undefined;
    const unsubscribe = onSnapshot(
      doc(db, 'settings', 'publicSite'),
      (snapshot) => {
        const saved = snapshot.exists() ? snapshot.data()?.facultiesContent : null;
        setFaculties(normalizeFacultiesContent(saved || defaultFaculties));
      },
      () => setFaculties(normalizeFacultiesContent(defaultFaculties))
    );
    return () => unsubscribe();
  }, [useFirebase]);

  useEffect(() => {
    if (!useFirebase) return undefined;
    return onSnapshot(
      collection(db, 'courses'),
      (snapshot) => setCourses(snapshot.docs.map((courseDoc) => ({ id: courseDoc.id, ...courseDoc.data() }))),
      () => setCourses([])
    );
  }, [useFirebase]);

  usePageMeta(
    faculty ? `${faculty.title} | School of Shem` : 'Faculty | School of Shem',
    faculty?.summary || 'Explore this School of Shem faculty.'
  );

  if (!faculty) {
    return (
      <PublicLayout>
        <PageHero
          eyebrow="Academics"
          title="Faculty not found"
          subtitle="The requested faculty is not available."
          actions={<Link to="/academics/faculties" className="btn btn-outline">View Academics</Link>}
        />
      </PublicLayout>
    );
  }

  const isMillionairesConnect = faculty.slug === 'millionaires-connect';
  const isPurchased = (productId) => purchasedProducts.some(
    (item) => item?.facultySlug === faculty.slug && item?.productId === productId
  );

  const handleAddToCart = (product) => {
    const selectedFormat = product.format === 'both'
      ? (productFormats[product.id] || 'physical')
      : (product.format || 'physical');
    addToCart({
      id: `${faculty.slug}-${product.id}-${selectedFormat}`,
      productId: product.id,
      title: product.title,
      description: product.description,
      image: product.image,
      price: Number(product.price) || 0,
      facultySlug: faculty.slug,
      facultyTitle: faculty.title,
      format: selectedFormat,
      pdfUrl: product.pdfUrl || ''
    });
    setAddedProductId(product.id);
    window.setTimeout(() => setAddedProductId(''), 1500);
  };

  const highlights = faculty.details?.highlights || [];
  const learning = faculty.details?.learning || [];
  const outcomes = faculty.details?.outcomes || [];
  const products = faculty.products || [];
  const facultyCourses = courses.filter((course) => {
    if (course.published === false || course.isPublic === false || course.status === 'draft') return false;
    const courseFaculty = course.facultySlug || course.facultyId || course.faculty || course.facultyName;
    return [faculty.slug, faculty.title].map(slugify).includes(slugify(courseFaculty));
  });

  return (
    <PublicLayout>
      <PageHero
        eyebrow="Academic faculty"
        title={faculty.title}
        subtitle={faculty.summary}
        actions={(
          <>
            {isMillionairesConnect ? (
              <>
                <a
                  href={MILLIONAIRES_CONNECT_EXTERNAL_ENROLL_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-primary"
                >
                  Pay &amp; Register
                </a>
                <Link to="/student-signup" className="btn btn-outline">Enter Admissions Code</Link>
              </>
            ) : (
              <Link to={withBase(APPLY_ROUTE)} className="btn btn-primary">Apply Now</Link>
            )}
          </>
        )}
      >
        {faculty.logo && (
          <div className="public-faculty-hero-media">
            <img src={faculty.logo} alt="" />
          </div>
        )}
      </PageHero>

      <section className="ps-section">
        <div className="ps-section-inner public-faculty-overview">
          <div className="public-prose">
            <div className="ps-hero-eyebrow">Overview</div>
            <h2>About this faculty</h2>
            <p>{faculty.details?.overview || faculty.summary}</p>
          </div>
          {highlights.length > 0 && (
            <aside className="public-detail-aside" aria-label="Faculty highlights">
              <h2>Highlights</h2>
              <ul>
                {highlights.map((item) => <li key={item}>{item}</li>)}
              </ul>
            </aside>
          )}
        </div>
      </section>

      <section className="ps-section ps-section-muted">
        <div className="ps-section-inner">
          <div className="ps-section-heading-row">
            <div>
              <div className="ps-hero-eyebrow">Study pathways</div>
              <h2 className="ps-section-title">Courses in this faculty</h2>
            </div>
          </div>
          {facultyCourses.length > 0 ? (
            <div className="public-course-list">
              {facultyCourses.map((course) => {
                const applicationsOpen = course.openForSignup !== false && ['open', 'active'].includes(course.status || 'open');
                return (
                  <article className="public-course-row" key={course.id}>
                    <div>
                      <span>{course.code || 'Course'}</span>
                      <h3>{course.title || course.name}</h3>
                      {(course.summary || course.description) && <p>{course.summary || course.description}</p>}
                    </div>
                    <dl>
                      {course.intake && <><dt>Intake</dt><dd>{course.intake}</dd></>}
                      <dt>Applications</dt><dd>{applicationsOpen ? 'Open' : 'Closed'}</dd>
                    </dl>
                    {applicationsOpen ? (
                      <Link to={withBase(APPLY_ROUTE)} className="btn btn-primary btn-sm">Apply Now</Link>
                    ) : (
                      <span className="public-course-status">Applications closed</span>
                    )}
                  </article>
                );
              })}
            </div>
          ) : (
            <p className="ps-empty-state">Courses for this faculty will be published here when enrolment opens.</p>
          )}
        </div>
      </section>

      {isMillionairesConnect && (
        <section className="ps-section public-mc-curriculum">
          <div className="ps-section-inner">
            <div className="ps-section-heading-row">
              <div>
                <div className="ps-hero-eyebrow">Recorded programme</div>
                <h2 className="ps-section-title">{millionairesConnectLessons.length} guided video lessons</h2>
                <p className="public-mc-curriculum-intro">The complete recorded learning path is included for enrolled students. Lessons are watched in order inside the protected student classroom.</p>
              </div>
              <Link to="/login" className="btn btn-primary">Student Sign In</Link>
            </div>
            <ol className="public-mc-lesson-list">
              {millionairesConnectLessons.map((lesson) => (
                <li key={lesson.id}>
                  <span>{String(lesson.order).padStart(2, '0')}</span>
                  <strong>{lesson.title}</strong>
                  <em>Student access</em>
                </li>
              ))}
            </ol>
          </div>
        </section>
      )}

      {(learning.length > 0 || outcomes.length > 0 || faculty.details?.outcome) && (
        <section className="ps-section">
          <div className="ps-section-inner public-learning-columns">
            {learning.length > 0 && (
              <div className="public-prose">
                <div className="ps-hero-eyebrow">Curriculum</div>
                <h2>What you will learn</h2>
                <ul>
                  {learning.map((item) => <li key={item}>{item}</li>)}
                </ul>
              </div>
            )}
            {(outcomes.length > 0 || faculty.details?.outcome) && (
              <div className="public-prose">
                <div className="ps-hero-eyebrow">Formation</div>
                <h2>Outcomes</h2>
                {outcomes.length > 0 ? (
                  <ul>{outcomes.map((item) => <li key={item}>{item}</li>)}</ul>
                ) : (
                  <p>{faculty.details?.outcome}</p>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      {products.length > 0 && (
        <section className="ps-section">
          <div className="ps-section-inner">
            <div className="ps-section-heading-row">
              <div>
                <div className="ps-hero-eyebrow">Books and courses</div>
                <h2 className="ps-section-title">Continue your learning</h2>
              </div>
              <Link to={withBase('/cart')} className="public-text-link">
                View cart <span aria-hidden="true">→</span>
              </Link>
            </div>
            <div className="public-product-list">
              {products.map((product) => {
                const purchased = isPurchased(product.id);
                const youtubeEmbed = toYoutubeEmbed(product.videoUrl);
                return (
                  <article className="public-product" key={product.id}>
                    <div className="public-product-media">
                      <img src={product.image} alt="" loading="lazy" />
                    </div>
                    <div className="public-product-copy">
                      <span>{product.type} · {product.format || 'physical'}</span>
                      <h3>{product.title}</h3>
                      <p>{product.description}</p>
                      <strong>R{Number(product.price || 0).toLocaleString('en-ZA')}</strong>
                      <div className="public-product-actions">
                        {product.format === 'both' && (
                          <label className="public-format-field">
                            <span>Format</span>
                            <select
                              value={productFormats[product.id] || 'physical'}
                              onChange={(event) => setProductFormats((previous) => ({
                                ...previous,
                                [product.id]: event.target.value
                              }))}
                            >
                              <option value="physical">Physical</option>
                              <option value="pdf">PDF</option>
                            </select>
                          </label>
                        )}
                        <button type="button" className="btn btn-primary btn-sm" onClick={() => handleAddToCart(product)}>
                          {addedProductId === product.id ? 'Added to Cart' : 'Add to Cart'}
                        </button>
                        {product.pdfUrl && purchased && (
                          <a className="btn btn-outline btn-sm" href={product.pdfUrl} target="_blank" rel="noreferrer">
                            Open PDF
                          </a>
                        )}
                      </div>
                    </div>
                    {product.videoUrl && (
                      <div className="public-product-access">
                        {purchased && youtubeEmbed ? (
                          <iframe
                            title={`${product.title} video`}
                            src={youtubeEmbed}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        ) : purchased ? (
                          <video controls src={product.videoUrl} />
                        ) : (
                          <p>Video access unlocks after purchase.</p>
                        )}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <section className="ps-section public-cta">
        <div className="ps-section-inner public-cta-inner">
          <div>
            <h2>Ready to begin?</h2>
            <p>Take the next step into structured, practical learning.</p>
          </div>
          <div className="ps-hero-actions">
            <Link to={withBase('/academics/faculties')} className="btn btn-outline">All Faculties</Link>
            <Link to={withBase(APPLY_ROUTE)} className="btn btn-primary">Apply Now</Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
